/**
 * UltraAdminTopbar
 * ────────────────
 * Shared top navigation bar for every Ultra Admin sub-page.
 * Matches the style of User Management / Tenant Management etc.
 *
 * Props:
 *   title (string)        — page title (bilingual already resolved by caller)
 *   subtitle (string)     — optional small subtitle
 *   icon (LucideIcon)     — icon shown inside the round badge
 *   onRefresh (func)      — optional; shows refresh button when provided
 *   rightExtra (ReactNode) — optional extra buttons to show before language/logout
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { RefreshCw, Bell, LogOut, X, Package } from 'lucide-react';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UltraAdminTopbar({ title, subtitle, icon: Icon, onRefresh, rightExtra }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [unread, setUnread]         = useState(0);
  const [notifs, setNotifs]         = useState([]);
  const [open, setOpen]             = useState(false);
  const popoverRef                  = useRef(null);

  const getAuth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token')}` }
  });

  const fetchUnread = async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, getAuth());
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  };
  const fetchList = async () => {
    try {
      const r = await axios.get(`${API}/notifications/?limit=15`, getAuth());
      // Backend returns { items: [...] }
      setNotifs(Array.isArray(r.data?.items) ? r.data.items : (Array.isArray(r.data) ? r.data : []));
    } catch { /* silent */ }
  };

  // Initial + 30s polling for unread badge
  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const handleBellClick = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      await fetchList();
    }
  };

  const handleNotifClick = async (n) => {
    try {
      await axios.put(`${API}/notifications/${n.id}/read`, {}, getAuth());
    } catch { /* silent */ }
    setOpen(false);
    fetchUnread();
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, getAuth());
      setUnread(0);
      fetchList();
    } catch { /* silent */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('cop31_ultra_token');
    localStorage.removeItem('cop31_ultra_user');
    navigate('/giris?tab=ultra');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="px-6">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
                <Icon className="h-5 w-5 text-purple-700" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-gray-900" data-testid="ultra-topbar-title">{title}</h1>
              <p className="text-xs text-gray-500">
                {subtitle || `COP 31 ${tr ? 'Platform Yönetimi' : 'Platform Management'}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {rightExtra}
            {onRefresh && (
              <Button variant="ghost" size="sm" className="rounded-full" onClick={onRefresh} data-testid="ultra-topbar-refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <div className="relative" ref={popoverRef}>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full relative"
                data-testid="ultra-topbar-bell"
                onClick={handleBellClick}
              >
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span
                    className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white animate-pulse"
                    data-testid="ultra-topbar-bell-badge"
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </Button>

              {open && (
                <div
                  className="absolute right-0 top-12 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                  data-testid="ultra-topbar-bell-popover"
                >
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-bold text-sm text-gray-900">
                      {tr ? 'Bildirimler' : 'Notifications'}
                      {unread > 0 && (
                        <span className="ml-2 text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                          {unread} {tr ? 'yeni' : 'new'}
                        </span>
                      )}
                    </h3>
                    {unread > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800"
                        data-testid="ultra-topbar-mark-all-read"
                      >
                        {tr ? 'Tümünü Okundu' : 'Mark all read'}
                      </button>
                    )}
                  </div>
                  <div className="max-h-[28rem] overflow-y-auto divide-y divide-gray-50">
                    {notifs.length === 0 ? (
                      <div className="text-center py-10 text-xs text-gray-400">
                        {tr ? 'Yeni bildirim yok' : 'No new notifications'}
                      </div>
                    ) : notifs.map(n => (
                      <button
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex gap-3 ${!n.is_read ? 'bg-indigo-50/40' : ''}`}
                        data-testid={`ultra-topbar-notif-${n.id}`}
                      >
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          (n.type || '').startsWith('stand_request') ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Package className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-gray-900 leading-tight">{n.title}</p>
                          {n.message && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>}
                          <p className="text-[10px] text-gray-400 mt-1">
                            {new Date(n.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {!n.is_read && <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0 mt-1.5" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <LanguageToggle variant="admin" />
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="ultra-topbar-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {tr ? 'Çıkış' : 'Logout'}
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}
